import { LegalLinks } from "./Legal";
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Star, ArrowRight, Layers, Calculator, PenTool, Wallet, Activity, ShieldCheck, Flame,
} from "lucide-react";
import { api } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import PricingTable from "../components/PricingTable";

const FEATURES = [
  { icon: Layers, title: "Pacchetti & Add-on interattivi", desc: "Il cliente compone la sua offerta e vede il totale aggiornarsi in tempo reale." },
  { icon: Calculator, title: "Motore Fiscale Italiano", desc: "Forfettario, IVA, Rivalsa INPS/Cassa, bollo e ritenuta calcolati in automatico." },
  { icon: PenTool, title: "Firma su schermo & PDF A4", desc: "Accettazione firmata, certificato e PDF professionale in un clic." },
  { icon: Wallet, title: "Acconto e Saldo", desc: "Il cliente vede subito acconto e saldo residuo, tu segni gli incassi con un clic." },
  { icon: Activity, title: "Tracciamento & Solleciti", desc: "Scopri quando il cliente apre il preventivo e sollecita su WhatsApp." },
  { icon: ShieldCheck, title: "Listino Riutilizzabile", desc: "Salva i tuoi servizi e importali nei preventivi con un clic." },
];

function Stars({ n }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} style={{ width: 15, height: 15 }} className={i <= n ? "text-amber-400" : "text-slate-600"} fill={i <= n ? "currentColor" : "none"} />
      ))}
    </span>
  );
}

function useCountUp(target, duration = 1200) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (target == null) return;
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return value;
}

function FounderCounter({ config }) {
  const seatsLeft = config?.founder_seats_left ?? 0;
  const seatsTotal = config?.founder_seats_total ?? 50;
  const animated = useCountUp(seatsLeft);
  if (!config || !config.founder_enabled || config.founder_sold_out || seatsLeft <= 0) return null;
  return (
    <div
      data-testid="founder-hero-counter"
      className="inline-flex items-center gap-2.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 mb-6 qf-pop"
    >
      <Flame style={{ width: 16, height: 16 }} className="text-amber-400 animate-pulse" fill="currentColor" />
      <span className="text-sm font-semibold text-amber-200">
        Restano <span data-testid="founder-seats-left-value" className="font-mono font-extrabold text-white tabular-nums">{animated}</span> posti Fondatore
        <span className="text-amber-300/70 font-normal"> su {seatsTotal}</span>
      </span>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [billingConfig, setBillingConfig] = useState(null);

  useEffect(() => {
    api.get("/public/testimonials").then((r) => setTestimonials(r.data)).catch(() => setTestimonials([]));
    api.get("/public/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
    api.get("/billing/config").then((r) => setBillingConfig(r.data)).catch(() => setBillingConfig(null));
  }, []);

  const allReviews = [
    ...testimonials,
    ...reviews.map((r) => ({ rating: r.rating, testimonial_text: r.comment || "Ottimo strumento!", user_name: r.name, profession: r.role, created_at: r.created_at })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const avgRating = allReviews.length ? allReviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / allReviews.length : 0;

  return (
    <div data-testid="landing-page" className="min-h-screen bg-card">
      {/* Nav */}
      <header className="sticky top-0 z-20 bg-background/80 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo size="md" />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" data-testid="landing-login-button" onClick={() => navigate("/login")}>Accedi</Button>
            <Button data-testid="landing-cta-nav" onClick={() => navigate("/register")} className="font-semibold">Inizia gratis</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="relative max-w-6xl mx-auto px-5 py-24 sm:py-32">
          {!billingConfig?.founder_private && <FounderCounter config={billingConfig} />}
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-indigo-200 mb-6">
            <Star style={{ width: 13, height: 13 }} fill="currentColor" /> Il preventivo che si vende da solo
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl leading-[1.05]">
            Preventivi interattivi per freelance e agenzie italiane.
          </h1>
          <p className="text-lg text-slate-300 mt-6 max-w-xl">
            Crea offerte dinamiche con add-on, pacchetti e calcolo fiscale automatico. Il cliente personalizza e firma online, con acconto e saldo sempre chiari.
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Button data-testid="landing-cta-hero" onClick={() => navigate("/register")} className="h-12 px-6 text-base font-semibold bg-card text-slate-900 hover:bg-indigo-50">
              Inizia gratis <ArrowRight style={{ width: 18, height: 18 }} className="ml-2" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/login")} className="h-12 px-6 text-base font-semibold border-white/30 text-white bg-transparent hover:bg-white/10">
              Accedi al tuo account
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <h2 className="text-lg md:text-lg font-bold text-slate-900">Tutto quello che ti serve per chiudere più clienti</h2>
        <p className="text-slate-500 mt-2 max-w-2xl">Dallo studio del preventivo all'incasso dell'acconto, MyQuoteFlow è pensato per il professionista italiano.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-200/80 p-6 hover:border-primary/30 hover:shadow-sm transition-all">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <f.icon style={{ width: 22, height: 22 }} className="text-primary" />
              </div>
              <h3 className="font-semibold text-slate-900">{f.title}</h3>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" data-testid="landing-pricing-section" className="max-w-6xl mx-auto px-5 py-20">
        <div className="text-center mb-12">
          <h2 className="text-lg md:text-lg font-bold text-slate-900">Prezzi semplici e trasparenti</h2>
          <p className="text-slate-500 mt-2">Inizia gratis. Passa a Pro quando vuoi.{billingConfig?.founder_private ? "" : " Offerta Fondatore a tempo limitato."}</p>
        </div>
        <PricingTable config={billingConfig} onSelect={() => navigate("/register")} />
      </section>

      {/* Testimonials */}
      {allReviews.length > 0 && (
        <section data-testid="testimonials-section" className="bg-slate-50 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-5 py-20">
            <div className="text-center mb-12">
              <h2 className="text-lg md:text-lg font-bold text-slate-900">Cosa dicono i professionisti italiani</h2>
              <div data-testid="reviews-average" className="inline-flex items-center gap-2 mt-3 rounded-full bg-card border border-slate-200 px-4 py-1.5 shadow-sm">
                <Stars n={Math.round(avgRating)} />
                <span className="font-mono font-bold text-slate-900" data-testid="reviews-average-value">{avgRating.toFixed(1).replace(".", ",")} / 5</span>
                <span className="text-sm text-slate-500" data-testid="reviews-count">· {allReviews.length} recensioni verificate</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {allReviews.map((t, i) => (
                <div key={i} data-testid={`testimonial-card-${i}`} className="bg-card rounded-2xl border border-slate-200/80 p-6 shadow-sm">
                  <Stars n={t.rating} />
                  <p className="text-slate-700 mt-4 leading-relaxed">"{t.testimonial_text}"</p>
                  <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100">
                    <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                      {(t.user_name || "?").charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm leading-tight">{t.user_name}</p>
                      <p className="text-xs text-slate-500">{t.profession}{t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}` : ""}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-5 py-20 text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Pronto a inviare il tuo prossimo preventivo?</h2>
        <p className="text-slate-500 mt-3">Inizia gratis in meno di un minuto.</p>
        <Button data-testid="landing-cta-footer" onClick={() => navigate("/register")} className="mt-7 h-12 px-7 text-base font-semibold">
          Inizia gratis <ArrowRight style={{ width: 18, height: 18 }} className="ml-2" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-500">
            <BrandLogo size="sm" variant="mark" />
            <span className="text-sm">© {new Date().getFullYear()} MyQuoteFlow</span>
          </div>
          <div className="flex items-center justify-center flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500">
            <LegalLinks />
            <Link to="/login" className="hover:text-slate-900">Accedi</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
